import { isSalesViewEnabled } from '@ab-core-functions/sales-view';
import { userHasOnlySalesView } from '@ab-core-functions/user-permission';
import type { Query, QueryPositionPriceObjectsArgs } from '@ab-core/graphql/dist';
import { useAuth } from '@ab-core/hooks';
import type { ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getErrorLoggerFunction } from '../utils';
import { POSITION_PRICE_OBJECTS } from './gql/positionPriceObjects';
import { fetchPositionPriceObjectsInBatches } from './positionPriceObjectBatches';

type QueryDataType = Pick<Query, 'positionPriceObjects'>;
type OutgoingDataType = QueryDataType['positionPriceObjects'];

const onError = getErrorLoggerFunction('positionPriceObjects');

export const usePositionPriceObjects = (props: QueryPositionPriceObjectsArgs) => {
    const { hidePrice, positions, projectId } = props;
    const [outgoingData, setOutgoingData] = useState<OutgoingDataType>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const requestId = useRef(0);
    const client = useApolloClient();

    const { user } = useAuth();
    const hasUser = !!user;
    const accountNumber = user?.activeAccount?.accountNumber || '';
    const onlySalesView = userHasOnlySalesView();
    const isSalesView = onlySalesView || isSalesViewEnabled(accountNumber);

    const positionsKey = JSON.stringify(positions || []);
    const stablePositions = useMemo(
        () => JSON.parse(positionsKey) as NonNullable<QueryPositionPriceObjectsArgs['positions']>,
        [positionsKey]
    );

    const fetchPositionPriceObjects = useCallback(
        async (networkOnly = false) => {
            requestId.current += 1;
            const currentRequestId = requestId.current;

            if (!hasUser || stablePositions.length === 0) {
                setOutgoingData(undefined);
                setLoading(false);
                setError(false);
                setSuccess(false);

                return undefined;
            }

            // Vorhandene Preise beim Nachladen stehen lassen, sonst flackern die
            // Preisanzeigen der Consumer bei jedem refetch (z.B. Sales-View-Wechsel).
            setLoading(true);
            setError(false);
            setSuccess(false);

            try {
                const positionPriceObjects = await fetchPositionPriceObjectsInBatches(
                    stablePositions,
                    async (batch) => {
                        const { data } = await client.query<QueryDataType, QueryPositionPriceObjectsArgs>({
                            query: POSITION_PRICE_OBJECTS,
                            variables: {
                                hidePrice: hidePrice || isSalesView,
                                positions: batch,
                                projectId
                            },
                            fetchPolicy: networkOnly ? 'network-only' : 'cache-first'
                        });

                        return data.positionPriceObjects;
                    }
                );

                if (requestId.current === currentRequestId) {
                    setOutgoingData(positionPriceObjects);
                    setSuccess(true);
                }

                return positionPriceObjects;
            } catch (queryError) {
                onError(queryError as ApolloError);

                if (requestId.current === currentRequestId) {
                    setOutgoingData(undefined);
                    setError(true);
                    setSuccess(false);
                }

                return undefined;
            } finally {
                if (requestId.current === currentRequestId) {
                    setLoading(false);
                }
            }
        },
        [client, hasUser, hidePrice, isSalesView, projectId, stablePositions]
    );

    useEffect(() => {
        void fetchPositionPriceObjects();

        return () => {
            requestId.current += 1;
        };
    }, [fetchPositionPriceObjects]);

    const fetchPositionPriceObjectsRef = useRef(fetchPositionPriceObjects);

    useEffect(() => {
        fetchPositionPriceObjectsRef.current = fetchPositionPriceObjects;
    }, [fetchPositionPriceObjects]);

    const refetch = useCallback(() => {
        return fetchPositionPriceObjectsRef.current(true);
    }, []);

    return {
        positionPriceObjects: outgoingData,
        error,
        success,
        loading,
        refetch
    };
};
